import classNames from 'classnames';
import { isPast, subHours } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconChevronRight } from '#src/atoms/icons/system/iconChevronRight.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

import { MarketCreditExpirationGauge } from './MarketCreditExpirationGauge/index.tsx';

const Root = styled(Horizontal)`
  padding: var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);

  &.lastDay {
    background: linear-gradient(
      96deg,
      rgba(240, 201, 86, 0.16) 0%,
      rgba(240, 201, 86, 0.03) 100%
    );
  }
  &.sidebarCollapsed {
    border: none;
    justify-content: center;
  }

  &:not(.sidebarCollapsed):hover {
    background-color: var(--c-nd-50);
    border-color: var(--c-nd-300);
  }
  transition:
    300ms background-color ease-in-out,
    300ms border-color ease-in-out;
`;
const Middle = styled(Vertical)`
  flex: 1;
  text-align: left;
`;

const GAUGE_FULL_DAYS = 30;

const expirationGaugePercentage = (expiration: Date): number => {
  const daysLeft = (expiration.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return Math.min(100, Math.max(0, (daysLeft / GAUGE_FULL_DAYS) * 100));
};

type Props = { sidebarCollapsed?: boolean };
export const MarketCredits = ({ sidebarCollapsed }: Props) => {
  const { formatDistanceToNow } = useIntlContext();
  const { currentUser } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const maxDiscount = currentUser?.conversionCreditsMaxDiscount
    ? toMonetaryAmount(currentUser.conversionCreditsMaxDiscount)
    : MonetaryAmount.zero;
  // dateTypePolicy types this as Date, but a raw ISO string can still leak through.
  const soonestExpiration = currentUser?.conversionCreditsSoonestExpiration
    ? new Date(currentUser.conversionCreditsSoonestExpiration)
    : null;
  const { main } = useAmountWithConversion({ monetaryAmount: maxDiscount });

  if (maxDiscount.eurCents === 0) {
    return null;
  }

  const lastDay =
    !!soonestExpiration && isPast(subHours(soonestExpiration, 24));

  return (
    <Root className={classNames({ lastDay, sidebarCollapsed })}>
      <MarketCreditExpirationGauge
        percentage={
          soonestExpiration ? expirationGaugePercentage(soonestExpiration) : 0
        }
      />
      {!sidebarCollapsed && (
        <>
          <Middle gap={0.5}>
            <Horizontal>
              <LabelS bold>{main}</LabelS>
            </Horizontal>
            {!!soonestExpiration && (
              <LabelS
                color={lastDay ? 'var(--c-yellow-400)' : 'var(--c-nd-600)'}
              >
                <FormattedMessage
                  defaultMessage="{timeLeft} left"
                  values={{
                    timeLeft: formatDistanceToNow(soonestExpiration, {
                      addSuffix: false,
                    }),
                  }}
                />
              </LabelS>
            )}
          </Middle>
          <FontAwesomeIcon
            icon={iconChevronRight}
            size="sm"
            color="var(--c-nd-600)"
          />
        </>
      )}
    </Root>
  );
};
